import { useState, useRef, useEffect } from 'react'
import { useGenerationStore } from '../store/generationStore'
import { useAuthStore } from '../store/authStore'
import { projectsApi, styleRefsApi } from '../lib/api'
import type { StyleReference, Project } from '../types'
import { StyleRefPicker } from './StyleRefPicker'
import { ProjectSelector } from './ProjectSelector'
import { PresetBackgroundButton } from './PresetBackgroundButton'
import { presetBackgrounds, type PresetBackground } from '../data/presetBackgrounds'

// Pro tips that cycle in the sidebar
const proTips = [
  {
    title: '5-Shot for A/B Testing',
    description: 'Use 5-Shot mode to generate multiple variations at once. Test different versions on your listings to see which converts best.',
    link: null,
  },
  {
    title: 'White Background Mode',
    description: 'Many marketplaces require white backgrounds. Toggle "White BG" mode for clean, compliant product shots — no style reference needed.',
    link: null,
  },
  {
    title: 'Match Style to Your Brand',
    description: 'Browse the Style Library to find scenes that match your brand aesthetic. Consistency across listings builds customer trust.',
    link: '/styles',
  },
  {
    title: 'Not Photo Editing',
    description: "Kalypso doesn't edit your photos — it creates brand-new professional product photography. Same product, entirely new scene.",
    link: null,
  },
  {
    title: 'Autopilot is Always On',
    description: 'Kalypso automatically analyzes your style reference and optimizes lighting, camera angle, and composition for the best results.',
    link: null,
  },
]

interface AIStudioProps {
  styleRefs: StyleReference[]
}

export function AIStudio({ styleRefs }: AIStudioProps) {
  const { user, refreshUser } = useAuthStore()
  const {
    inputs,
    setInput,
    isGenerating,
    startGeneration,
  } = useGenerationStore()

  const [error, setError] = useState<string | null>(null)
  const [showStylePicker, setShowStylePicker] = useState(false)
  const [selectedStyleRef, setSelectedStyleRef] = useState<StyleReference | null>(null)
  const [currentTipIndex, setCurrentTipIndex] = useState(0)
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [loadingProjects, setLoadingProjects] = useState(true)
  const [selectedPreset, setSelectedPreset] = useState<PresetBackground | null>(null)
  const [isLoadingStyleRef, setIsLoadingStyleRef] = useState(false)
  const [showPresetsDropdown, setShowPresetsDropdown] = useState(false)
  const [showMoreOptions, setShowMoreOptions] = useState(false)
  const productInputRef = useRef<HTMLInputElement>(null)
  const presetsDropdownRef = useRef<HTMLDivElement>(null)

  // Cycle through tips every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % proTips.length)
    }, 8000)
    return () => clearInterval(interval)
  }, [])

  // Close presets dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (presetsDropdownRef.current && !presetsDropdownRef.current.contains(event.target as Node)) {
        setShowPresetsDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await projectsApi.getAll()
        setProjects(data)
      } catch (err) {
        console.error('Failed to load projects:', err)
      } finally {
        setLoadingProjects(false)
      }
    }
    fetchProjects()
  }, [])

  // Handle product image upload
  const handleProductUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB')
      return
    }

    const reader = new FileReader()
    reader.onload = () => {
      setInput('productImage', reader.result as string)
      setError(null)
    }
    reader.readAsDataURL(file)
  }

  // Handle style reference selection
  const handleStyleSelect = async (styleRef: StyleReference, base64?: string) => {
    setSelectedStyleRef(styleRef)
    // Clear preset selection (mutually exclusive)
    setSelectedPreset(null)
    // Clear any previous error
    setError(null)

    // If base64 provided (new upload), use it directly
    if (base64) {
      setInput('backgroundReference', base64)
      setShowStylePicker(false)
      return
    }

    // Otherwise fetch via backend proxy (avoids CORS issues with R2)
    setIsLoadingStyleRef(true)
    try {
      const { base64: fetchedBase64 } = await styleRefsApi.getImageData(styleRef.id)
      setInput('backgroundReference', fetchedBase64)
    } catch (err) {
      console.error('Failed to load style reference:', err)
      setError('Failed to load style reference image. Please try uploading a new one.')
    } finally {
      setIsLoadingStyleRef(false)
      setShowStylePicker(false)
    }
  }

  // Handle preset background selection (instant - base64 is pre-embedded)
  const handlePresetSelect = (preset: PresetBackground) => {
    // Toggle off if already selected
    if (selectedPreset?.id === preset.id) {
      setSelectedPreset(null)
      setInput('backgroundReference', undefined)
      return
    }

    setSelectedPreset(preset)
    // Clear style reference (mutually exclusive)
    setSelectedStyleRef(null)
    // Clear any previous error
    setError(null)
    // Use pre-embedded base64 (instant, no network request)
    setInput('backgroundReference', preset.base64)
  }

  // Handle generation
  const handleGenerate = async () => {
    setError(null)

    // Validate inputs
    if (!inputs.productDescription && !inputs.productImage) {
      setError('Please provide a product description or upload a product image')
      return
    }

    if (inputs.autoMode && !inputs.backgroundReference && !inputs.whiteBackgroundMode) {
      setError('Auto mode requires a style reference (unless using white background)')
      return
    }

    // Check available credits (subscription + bonus - reserved)
    const creditsNeeded = inputs.fiveShotMode ? 5 : 1
    const availableCredits = user?.availableCredits ?? user?.credits ?? 0
    if (availableCredits < creditsNeeded) {
      setError(`You need ${creditsNeeded} credits. You have ${availableCredits}.`)
      return
    }

    try {
      await startGeneration(selectedProject?.id, selectedStyleRef?.id)
      refreshUser() // Refresh to update credits
    } catch (err: any) {
      setError(err.message)
    }
  }

  return (
    <>
      <div className="w-full rounded-2xl overflow-hidden relative group shadow-soft bg-white border border-border-color">
        <div className="absolute inset-0 bg-gradient-to-r from-surface-soft via-white to-white z-0"></div>
        <div className="relative z-10 flex flex-col lg:flex-row p-8 lg:p-10 gap-10">
          {/* Left Column - Form */}
          <div className="flex flex-col justify-center gap-6 flex-1">
            {/* Badge */}
            <div className="flex items-center gap-2 text-primary-dark font-bold text-xs tracking-widest uppercase">
              <span className="material-symbols-outlined text-lg">auto_awesome</span>
              Product Photography Software
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-serif text-text-main mb-3">
                Create new product photos
              </h2>
              <p className="text-text-muted max-w-lg leading-relaxed">
                Upload your product and choose a style reference. Kalypso creates brand-new,
                studio-quality listing images in seconds.
              </p>
            </div>

            {/* Form */}
            <div className="bg-surface-soft rounded-xl p-5 border border-border-color flex flex-col gap-5">
              {/* Hidden file input */}
              <input
                ref={productInputRef}
                type="file"
                accept="image/*"
                onChange={handleProductUpload}
                className="hidden"
              />

              {/* Two Column Grid: Product Image + Style Reference */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 1. Product Image */}
                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold uppercase text-text-muted ml-1">
                    1. Product Image
                  </label>
                  {inputs.productImage ? (
                    <div className="relative group border border-border-color bg-white rounded-lg h-32 overflow-hidden">
                      <img
                        src={inputs.productImage}
                        alt="Product"
                        className="w-full h-full object-contain"
                      />
                      <button
                        onClick={() => {
                          setInput('productImage', undefined)
                          if (productInputRef.current) {
                            productInputRef.current.value = ''
                          }
                        }}
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 shadow-sm border border-gray-100 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                      >
                        <span className="material-symbols-outlined text-sm text-gray-600">close</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => productInputRef.current?.click()}
                      className="border border-dashed border-primary/30 bg-white rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all text-primary-dark upload-area-premium"
                    >
                      <span className="material-symbols-outlined text-2xl">add_a_photo</span>
                      <span className="text-xs font-medium mt-2">Upload Product</span>
                    </button>
                  )}
                </div>

                {/* 2. Style Reference with Presets Dropdown */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold uppercase text-text-muted ml-1">
                      2. Style Reference
                    </label>
                    {/* Presets Dropdown */}
                    <div className="relative" ref={presetsDropdownRef}>
                      <button
                        onClick={() => setShowPresetsDropdown(!showPresetsDropdown)}
                        className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition-all ${
                          showPresetsDropdown || selectedPreset
                            ? 'bg-primary/10 text-primary-dark'
                            : 'text-text-muted hover:bg-surface-muted hover:text-text-main'
                        }`}
                      >
                        <span className="material-symbols-outlined text-sm">
                          {selectedPreset ? 'check_circle' : 'grid_view'}
                        </span>
                        <span>{selectedPreset ? selectedPreset.name : 'Presets'}</span>
                        <span className={`material-symbols-outlined text-sm transition-transform ${showPresetsDropdown ? 'rotate-180' : ''}`}>
                          expand_more
                        </span>
                      </button>

                      {/* Presets Dropdown Menu */}
                      {showPresetsDropdown && (
                        <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg border border-border-color shadow-lg p-3 w-56 animate-fade-up">
                          <div className="text-xs font-semibold text-text-muted mb-2">Quick Backgrounds</div>
                          <div className="grid grid-cols-2 gap-2">
                            {presetBackgrounds.map((preset) => (
                              <PresetBackgroundButton
                                key={preset.id}
                                preset={preset}
                                isSelected={selectedPreset?.id === preset.id}
                                isLoading={false}
                                onClick={() => {
                                  handlePresetSelect(preset)
                                  setShowPresetsDropdown(false)
                                }}
                              />
                            ))}
                          </div>
                          {selectedPreset && (
                            <button
                              onClick={() => {
                                setSelectedPreset(null)
                                setInput('backgroundReference', undefined)
                                setShowPresetsDropdown(false)
                              }}
                              className="w-full mt-2 text-xs text-text-muted hover:text-red-600 py-1.5 rounded hover:bg-red-50 transition-colors"
                            >
                              Clear Selection
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Style Reference Display/Upload */}
                  {selectedStyleRef ? (
                    <div
                      onClick={() => setShowStylePicker(true)}
                      className="border border-border-color bg-white rounded-lg h-32 p-3 flex items-center gap-3 cursor-pointer hover:border-primary transition-all relative group/style"
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedStyleRef(null)
                          setInput('backgroundReference', undefined)
                        }}
                        className="absolute top-2 right-2 z-10 p-1 rounded-full bg-white/90 shadow-sm border border-gray-100 opacity-0 group-hover/style:opacity-100 transition-opacity hover:bg-white"
                      >
                        <span className="material-symbols-outlined text-sm text-gray-600">close</span>
                      </button>
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        <img
                          src={selectedStyleRef.imageUrl}
                          alt={selectedStyleRef.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-text-main text-sm block truncate">
                          {selectedStyleRef.name}
                        </span>
                        <span className="text-xs text-text-muted line-clamp-2">
                          {selectedStyleRef.description || 'Custom style reference'}
                        </span>
                        <span className="text-xs text-primary mt-1 inline-flex items-center gap-1">
                          <span className="material-symbols-outlined text-xs">edit</span>
                          Change
                        </span>
                      </div>
                    </div>
                  ) : selectedPreset ? (
                    <div className="border border-primary/30 bg-primary/5 rounded-lg h-32 p-3 flex items-center gap-3 relative">
                      <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: selectedPreset.fallbackColor }}>
                        <img
                          src={selectedPreset.url}
                          alt={selectedPreset.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="material-symbols-outlined text-sm text-primary">check_circle</span>
                          <span className="text-xs font-medium text-primary">Preset Active</span>
                        </div>
                        <span className="font-medium text-text-main text-sm block">
                          {selectedPreset.name}
                        </span>
                        <span className="text-xs text-text-muted">
                          {selectedPreset.description}
                        </span>
                      </div>
                      <button
                        onClick={() => setShowStylePicker(true)}
                        className="absolute bottom-3 right-3 text-xs text-primary-dark hover:underline"
                      >
                        Use custom instead
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowStylePicker(true)}
                      className="border border-dashed border-primary/30 bg-white rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all text-primary-dark upload-area-premium"
                    >
                      <span className="material-symbols-outlined text-2xl">palette</span>
                      <span className="text-xs font-medium mt-2">Choose Style</span>
                      <span className="text-[10px] text-text-muted mt-0.5">or select a preset above</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Generate Button - Full Width */}
              <button
                onClick={handleGenerate}
                disabled={isGenerating || isLoadingStyleRef}
                className="w-full bg-primary hover:bg-primary-dark text-white py-3.5 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-sm btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-xl">shutter_speed</span>
                <span>{isGenerating ? 'Generating...' : 'Generate'}</span>
                {!isGenerating && (
                  <span className="text-white/70 text-xs ml-1">
                    ({inputs.fiveShotMode ? '5' : '1'} credit{inputs.fiveShotMode ? 's' : ''})
                  </span>
                )}
              </button>

              {/* More Options Collapsible */}
              <div className="border-t border-border-color pt-3">
                <button
                  onClick={() => setShowMoreOptions(!showMoreOptions)}
                  className="w-full flex items-center justify-between py-2 text-sm text-text-muted hover:text-text-main transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-lg">
                      {showMoreOptions ? 'expand_less' : 'expand_more'}
                    </span>
                    <span className="font-medium">More Options</span>
                  </span>
                  {(inputs.productDescription || selectedProject) && !showMoreOptions && (
                    <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                      {[inputs.productDescription && 'Scene', selectedProject && 'Project'].filter(Boolean).join(' + ')} set
                    </span>
                  )}
                </button>

                {showMoreOptions && (
                  <div className="mt-3 space-y-4 animate-fade-up">
                    {/* Scene Description */}
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-semibold uppercase text-text-muted ml-1">
                        Describe the Scene (Optional)
                      </label>
                      <input
                        type="text"
                        value={inputs.productDescription}
                        onChange={(e) => setInput('productDescription', e.target.value)}
                        placeholder="e.g. minimalist marble counter, morning light"
                        className="w-full bg-white border border-border-color rounded-lg px-4 py-3 text-sm text-text-main placeholder:text-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none"
                      />
                    </div>

                    {/* Save to Project */}
                    <div className="flex flex-col gap-2">
                      <label className="text-xs font-semibold uppercase text-text-muted ml-1">
                        Save to Project (Optional)
                      </label>
                      <ProjectSelector
                        projects={projects}
                        selectedProject={selectedProject}
                        onSelect={setSelectedProject}
                        isLoading={loadingProjects}
                        onProjectCreated={(project) => {
                          setProjects([...projects, project])
                          setSelectedProject(project)
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Mode Toggles */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-border-color">
                {/* Autopilot - Always on, shown as indicator */}
                <div
                  className="badge bg-primary text-white py-1.5 px-3 select-none"
                  title="Autopilot automatically optimizes settings based on your style reference"
                >
                  <span className="material-symbols-outlined text-sm mr-1">toggle_on</span>
                  Autopilot
                </div>
                <button
                  onClick={() => setInput('fiveShotMode', !inputs.fiveShotMode)}
                  className={`badge ${
                    inputs.fiveShotMode ? 'bg-amber-600 text-white' : 'bg-blue-600 text-white'
                  } cursor-pointer py-1.5 px-3`}
                >
                  <span className="material-symbols-outlined text-sm mr-1">
                    {inputs.fiveShotMode ? 'filter_5' : 'filter_1'}
                  </span>
                  {inputs.fiveShotMode ? '5-Shot' : '1-Shot'}
                </button>
                <button
                  onClick={() => setInput('whiteBackgroundMode', !inputs.whiteBackgroundMode)}
                  className={`badge ${
                    inputs.whiteBackgroundMode ? 'bg-gray-800 text-white' : 'bg-surface-muted text-text-muted'
                  } cursor-pointer py-1.5 px-3`}
                >
                  <span className="material-symbols-outlined text-sm mr-1">
                    crop_free
                  </span>
                  White BG
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* Right Column - Pro Tip */}
          <div className="hidden lg:flex flex-col justify-center flex-1 max-w-sm pl-8 border-l border-border-color/50">
            <div className="bg-accent/20 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 text-accent/40 opacity-50">
                <span className="material-symbols-outlined text-9xl">tips_and_updates</span>
              </div>
              <div className="flex items-center gap-2 mb-3 relative z-10">
                <span className="text-xs font-bold text-primary uppercase tracking-wide">Pro Tip</span>
                <span className="text-xs text-text-muted">•</span>
                <span className="text-xs text-text-muted">{currentTipIndex + 1}/{proTips.length}</span>
              </div>
              <h3 className="font-serif font-bold text-lg text-primary-dark mb-3 relative z-10">
                {proTips[currentTipIndex].title}
              </h3>
              <p className="text-sm text-text-main/80 mb-4 relative z-10 leading-relaxed">
                {proTips[currentTipIndex].description}
              </p>
              {proTips[currentTipIndex].link && (
                <a
                  href={proTips[currentTipIndex].link}
                  className="text-xs font-bold text-primary uppercase tracking-wide hover:underline relative z-10"
                >
                  Browse Style Library →
                </a>
              )}
              {/* Tip navigation dots */}
              <div className="flex gap-1.5 mt-4 relative z-10">
                {proTips.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTipIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentTipIndex
                        ? 'bg-primary w-4'
                        : 'bg-primary/30 hover:bg-primary/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Style Reference Picker Modal */}
      <StyleRefPicker
        isOpen={showStylePicker}
        onClose={() => setShowStylePicker(false)}
        styleRefs={styleRefs}
        onSelect={handleStyleSelect}
      />
    </>
  )
}
